import { useState } from "react";
import { Button } from "@/components/ui/button";
import { EyeOff } from "lucide-react";
import { QuietTag, SearchBox } from "./AwaitingPaymentTable";

const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

const identText = (t) => {
  const i = t.identified_as;
  if (i) {
    const who = [i.sf_number, i.customer_name].filter(Boolean).join(" · ");
    const status = i.loan_status && i.loan_status !== "Open" ? ` · ${i.loan_status}` : "";
    return `${i.source === "arrears_list" ? "Arrears payment" : "Not due today"} — ${who}${status}`;
  }
  return t.category?.label || "";
};

export function UnmatchedTransactionsTable({ txns, categories, onIrrelevant }) {
  const [q, setQ] = useState("");
  const rows = txns
    .filter((t) => !q || `${t.description} ${t.reference || ""} ${t.amount}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.amount || 0) - (a.amount || 0));
  const total = txns.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
  const cats = Object.entries(categories || {}).sort((a, b) => b[1].count - a[1].count);
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="unmatched-panel">
      <header className="border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <h2 className="text-base font-semibold text-slate-900">Unmatched credits</h2>
            <p className="text-xs text-slate-500" data-testid="unmatched-summary">{txns.length} credits · <span className="font-mono tabular-nums">{gbp(total)}</span> not matched to a loan due today</p>
          </div>
          <div className="w-48"><SearchBox value={q} onChange={setQ} placeholder="Description or amount" testId="unmatched-search" /></div>
        </div>
        {cats.length > 0 && (
          <p className="mt-2 text-[11px] text-slate-500" data-testid="unmatched-category-summary">
            {cats.map(([k, c], i) => (
              <span key={k} data-testid={`category-summary-${k}`}>{i ? " · " : ""}{c.label} <span className="font-mono tabular-nums text-slate-700">{c.count}</span></span>
            ))}
          </p>
        )}
      </header>
      <div className="max-h-[560px] overflow-y-auto">
        <table className="w-full text-sm" data-testid="table-unmatched-transactions">
          <thead className="sticky top-0 z-10 border-b bg-slate-50 text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Credit</th><th className="px-3 py-2 text-right font-semibold">Amount £</th><th />
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.id} className="group border-b hover:bg-slate-50" data-testid={`row-unmatched-${t.id}`}>
                <td className="px-3 py-2 min-w-0">
                  <div className="font-medium text-slate-900 truncate max-w-[22rem]" title={t.description}>{t.description || "Bank transfer"}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                    {t.bank_account && <span>{t.bank_account}</span>}
                    {t.time_added && <span className="font-mono">{t.time_added}</span>}
                    {t.status === "Pending" && <QuietTag tone="amber">pending</QuietTag>}
                    <span data-testid={t.identified_as ? "txn-identified-badge" : "txn-category-badge"} className={t.category?.key === "unknown" ? "text-amber-700" : ""}>{identText(t)}</span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-slate-900 align-top">{gbp(t.amount)}</td>
                <td className="px-2 py-1 text-right align-top">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-slate-700" title="Mark as irrelevant (internal transfer)" onClick={() => onIrrelevant(t)} data-testid={`btn-mark-irrelevant-${t.id}`}>
                    <EyeOff className="w-3.5 h-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr><td colSpan={3} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="unmatched-empty">{q ? "No credits match the search" : "Every credit is matched"}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
